import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Tabs,
  Spinner,
  Alert,
} from '@cloudscape-design/components';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import docco from 'react-syntax-highlighter/dist/esm/styles/hljs/docco';
import apiService from '../services/api';

// Register the language
SyntaxHighlighter.registerLanguage('json', json);

function PromptResponseModal({ visible, onDismiss, fileId }) {
  const [activeTabId, setActiveTabId] = useState('prompt');
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible) {
      const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
          // Fetch prompt
          const promptData = await apiService.getBedrockPrompt();
          
          // Format the prompt with actual questions
          let formattedPrompt = promptData.prompt;
          
          // Replace placeholders with actual values
          if (fileId === 'file1') {
            formattedPrompt = formattedPrompt
              .replace('{document_name}', 'AusSuper Product Disclosure Statement')
              .replace('{question_1}', 'What is the cooling off period defined in the document if a customer changes their mind?')
              .replace('{question_2}', 'What is the fund\'s investment strategy for managing environmental, social, and governance (ESG) factors across different investment options?')
              .replace('{question_3}', 'Does the document have a section about how super contributions are taxed? Does it include explanations on how that works?')
              .replace('{question_4}', 'What fees are charged for the Balanced investment option?');
          } else if (fileId === 'file2') {
            formattedPrompt = formattedPrompt
              .replace('{document_name}', 'AwareSuper Product Disclosure Statement')
              .replace('{question_1}', 'What is the minimum initial investment amount for AwareSuper?')
              .replace('{question_2}', 'What is AwareSuper\'s approach to responsible investing?')
              .replace('{question_3}', 'What insurance options are available through AwareSuper?')
              .replace('{question_4}', 'What is the administration fee for AwareSuper\'s Growth option?');
          } else if (fileId === 'file3') {
            formattedPrompt = formattedPrompt
              .replace('{document_name}', 'Vanguard Super Product Disclosure Statement')
              .replace('{question_1}', 'What is Vanguard Super\'s investment philosophy?')
              .replace('{question_2}', 'What is the fee structure for Vanguard Super\'s Balanced Index option?')
              .replace('{question_3}', 'How often can members switch between investment options in Vanguard Super?')
              .replace('{question_4}', 'What is the target asset allocation for growth assets in Vanguard Super\'s Growth Index option?');
          }
          
          setPrompt(formattedPrompt);
          
          // Fetch response
          const responseData = await apiService.getBedrockResponse(fileId);
          setResponse(JSON.stringify(responseData, null, 2));
        } catch (err) {
          console.error('Error fetching prompt/response:', err);
          setError('Failed to load prompt and response data.');
        } finally {
          setLoading(false);
        }
      };
      
      fetchData();
    }
  }, [visible, fileId]);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header="Amazon Bedrock Prompt & Response"
      footer={
        <Box float="right">
          <Button onClick={onDismiss}>Close</Button>
        </Box>
      }
    >
      {loading ? (
        <Box textAlign="center" padding="l">
          <SpaceBetween size="m" direction="vertical" alignItems="center">
            <Spinner size="large" />
            <div>Loading prompt and response data...</div>
          </SpaceBetween>
        </Box>
      ) : error ? (
        <Alert type="error">{error}</Alert>
      ) : (
        <Tabs
          activeTabId={activeTabId}
          onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          tabs={[
            {
              id: 'prompt',
              label: 'Prompt',
              content: (
                <Box padding="m">
                  <div style={{ maxHeight: '500px', overflow: 'auto', border: '1px solid #eaeded', borderRadius: '4px' }}>
                    <SyntaxHighlighter language="plaintext" style={docco} customStyle={{ margin: 0, padding: '20px' }}>
                      {prompt}
                    </SyntaxHighlighter>
                  </div>
                </Box>
              )
            },
            {
              id: 'response',
              label: 'Response',
              content: (
                <Box padding="m">
                  <div style={{ maxHeight: '500px', overflow: 'auto', border: '1px solid #eaeded', borderRadius: '4px' }}>
                    <SyntaxHighlighter language="json" style={docco} customStyle={{ margin: 0, padding: '20px' }}>
                      {response}
                    </SyntaxHighlighter>
                  </div>
                </Box>
              )
            }
          ]}
        />
      )}
    </Modal>
  );
}

export default PromptResponseModal;
